import { APIError, useSubmit } from "@certn/api";
import { Card, Icon, showToast, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";

import { useTranslation } from "react-i18next";

import { doesCardHaveIcon, getPaymentIcon, isCardExpired } from "@certn/utils";
import { PaymentProfileActionDropdown } from "./PaymentProfileActionDropdown";

interface PaymentCardProps {
  id: string;
  brand: string | null;
  number: string | null;
  expiryDate: string | null;
  owner: string | null;
  issuer: string | null;
  status: "ACTIVE" | "INACTIVE" | "FAILED";
  mutate: () => void;
}

export function PaymentCard(props: PaymentCardProps) {
  const { brand, number, expiryDate, owner, id, status, mutate } = props;
  const { t } = useTranslation();

  const formattedExpiryDate = expiryDate
    ? `${expiryDate.split("-")[1]}/${expiryDate.split("-")[0]}`
    : "";

  const { submit: archiveProfile, isSubmitting: archiving } = useSubmit(
    `/api/internal/applicant/me/payment-profiles/${id}/archive/`,
    {
      onSuccess: async () => {
        await mutate();
        showToast(t("Payment profile archived"), { type: "success" });
      },
      onFailure: (error: unknown) => {
        // Extract error message from APIError (DRF format: { detail: "message" })
        if (error instanceof APIError && error.detail) {
          showToast(error.detail, { type: "error" });
        } else {
          showToast(
            t(
              "This card cannot be archived because one or more orders are currently in progress."
            ),
            { type: "error" }
          );
        }
      },
      showToastOnError: false, // We handle the toast in onFailure
    }
  );

  const { submit: activateProfile, isSubmitting: activating } = useSubmit(
    `/api/internal/applicant/me/payment-profiles/${id}/activate/`,
    {
      onSuccess: async () => {
        await mutate();
        showToast(t("Payment profile activated"), { type: "success" });
      },
    }
  );

  const { submit: deleteProfile, isSubmitting: deleting } = useSubmit(
    `/api/internal/applicant/me/payment-profiles/${id}/delete/`,
    {
      onSuccess: async () => {
        await mutate();
        showToast(t("Payment profile deleted"), { type: "success" });
      },
    }
  );

  return (
    <Card key={id} border="border-base" className={styles.card}>
      <div className={styles.leftGroup}>
        <div className={styles.icon}>
          {brand && doesCardHaveIcon(brand) ? (
            <img
              src={getPaymentIcon(brand)}
              alt={brand}
              width="24px"
              height="24px"
            />
          ) : (
            <Icon name="credit_card" size="space-6" />
          )}
        </div>
        <div className={styles.info}>
          {number && (
            <Text style="global/body-primary">
              ****
              {number}
            </Text>
          )}
          {brand && <Text style="global/body-secondary">{brand}</Text>}
          {expiryDate && (
            <div className={styles.bottomRow}>
              {isCardExpired(formattedExpiryDate) ? (
                <Text
                  style="global/body-secondary"
                  color="background-accent-red-strong"
                >
                  {t("Expired")} {formattedExpiryDate}
                </Text>
              ) : (
                <Text style="global/body-secondary">
                  {t("Expires")} {formattedExpiryDate}{" "}
                </Text>
              )}
              {owner && (
                <span className={styles.spacer}>
                  <Text style="global/body-secondary">
                    {" \u00B7 "} {owner}
                  </Text>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.actionDropdown}>
        <PaymentProfileActionDropdown
          profileId={id}
          status={status}
          handleArchive={() => archiveProfile()}
          handleActivate={() => activateProfile()}
          handleDelete={() => deleteProfile({}, { method: "DELETE" })}
          isArchiving={archiving}
          isActivating={activating}
          isDeleting={deleting}
        />
      </div>
    </Card>
  );
}

const styles = {
  leftGroup: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
  }),
  actionDropdown: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "end",
    margin: "auto 0",
  }),
  spacer: css({
    paddingLeft: "var(--space-1)",
  }),
  bottomRow: css({
    display: "flex",
    flexDirection: "row",
  }),
  card: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "var(--space-4)",
  }),
  icon: css({
    marginRight: "var(--space-3)",
  }),
  info: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    alignItems: "left",
  }),
};
